import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import useWebSocket from "react-use-websocket";
import { SessionContext } from "../types/sessionContext";
import type {
    Message,
    ResponseAudioDelta,
    ResponseAudioTranscriptDelta,
    ResponseDone,
    ExtensionMiddleTierToolResponse,
    // ResponseInputAudioTranscriptionDelta,
    ResponseInputAudioTranscriptionCompleted
} from "@/types";
import { getCurrentDemoConfig, createDemoSessionContext } from "../config/demoSites";
import { throttledLog } from "../utils/throttledLog"; // Add this import

/** Define which values we want to share via context. */
type RealtimeContextValue = {
    /** In case you need to send JSON commands, e.g. "session.update" or "input_audio_buffer.append" etc. */
    sendJsonMessage: (msg: Record<string, any>) => void;

    /** For any logic you want to expose about the connection, e.g. startSession, closeWebSocket, etc. */
    startSession: () => void;
    closeWebSocket: () => void;

    /** Example of storing certain messages in state and providing them to consumers. */
    latestToolResponse: ExtensionMiddleTierToolResponse | null;
    // You can add more fields as needed (like transcript, if you want shared across components).
    /** Track whether an interrupt message was received. */
    interruptReceived: boolean;
    sessionContext: SessionContext;
    updateSessionContext: (newContext: SessionContext | ((prevContext: SessionContext) => SessionContext), source?: string) => void;
    setSessionConnectionId: (connectionId: string | null) => void;
};

/** Create the actual context, with an empty default. */
const RealtimeContext = createContext<RealtimeContextValue | null>(null);

/**
 * Provider props: you can optionally accept a connectionId, or figure out how you want to manage it.
 */
type RealtimeProviderProps = {
    children: ReactNode;
    initialConnectionId?: string;
};

export function RealtimeProvider({ children, initialConnectionId }: RealtimeProviderProps) {
    // 1) Get current demo config based on route
    //const demoConfig = getCurrentDemoConfig();

    // Example: track the latest tool response in a state
    const [latestToolResponse, setLatestToolResponse] = useState<ExtensionMiddleTierToolResponse | null>(null);

    // Track "interrupt" messages so that any component can react.
    const [interruptReceived, setInterruptReceived] = useState(false);

    // 2) Initialize sessionContext with the right structure for this demo
    const [sessionContext, setSessionContext] = useState<SessionContext>(() => {
        const id = initialConnectionId || crypto.randomUUID();
        return createDemoSessionContext(id, window.location.pathname);
    });

    // 2) Build the WS endpoint from sessionContext.connectionId
    //    If the user changes it mid-session, we might need to re-connect, so we'll handle that in a useEffect
    const [wsEndpoint, setWsEndpoint] = useState(`/realtime?connection_id=${sessionContext.connectionId}`);
    //console.log("[RealtimeProvider] Using WebSocket endpoint:", wsEndpoint);

    // 3) Whenever connectionId changes, rebuild the endpoint + re-connect
    useEffect(() => {
        const newEndpoint = `/realtime?connection_id=${sessionContext.connectionId}`;
        setWsEndpoint(newEndpoint);
    }, [sessionContext.connectionId]);
    /**
     * Use `react-use-websocket` to handle the connection
     */
    const { sendJsonMessage, getWebSocket } = useWebSocket(wsEndpoint, {
        onOpen: () => {
            console.log("[RealtimeProvider] WS connection opened");
            // Reset interrupt state whenever a new connection is opened
            setInterruptReceived(false);
        },
        onClose: () => {
            console.log("[RealtimeProvider] WS connection closed");
        },
        onError: event => {
            console.error("[RealtimeProvider] WS error:", event);
        },
        onMessage: event => {
            // "Master" onMessage logic
            onMessageReceived(event);
        },
        shouldReconnect: () => true // reconnect on close/error
    });

    /**
     * Master handler for incoming messages.
     * You can parse and store them in context state, or call callbacks, etc.
     */
    function onMessageReceived(event: MessageEvent) {
        let message: Message;
        try {
            message = JSON.parse(event.data);
            // Replace with throttled log
            throttledLog("WS_MESSAGE", `Message received: ${message.type}`);
        } catch (err) {
            console.error("[RealtimeProvider] Failed to parse WebSocket message:", err);
            return;
        }

        switch (message.type) {
            case "extension.middle_tier_tool_response": {
                const toolMsg = message as ExtensionMiddleTierToolResponse;
                // Replace with throttled log
                throttledLog("TOOL_RESPONSE", `Received tool response: ${toolMsg.tool_name}`);

                try {
                    if (toolMsg.tool_name === "update_form_field") {
                        const parsedResult = JSON.parse(toolMsg.tool_result);
                        const { field, value } = parsedResult;

                        // Find which step this field belongs to based on configuration
                        const demoConfig = getCurrentDemoConfig();
                        let targetStepId = null;

                        // Find the step that contains this field
                        for (const step of demoConfig.steps) {
                            if (step.fields.some(f => f.fieldName === field)) {
                                targetStepId = step.id;
                                break;
                            }
                        }

                        if (!targetStepId) {
                            console.error(`[RealtimeProvider] Field "${field}" not found in any step configuration`);
                            return;
                        }

                        // Replace with throttled log
                        throttledLog("FORM_UPDATE", `Updating field "${field}" in step ${targetStepId} with value: "${value}"`);

                        // Update the session context
                        setSessionContext(prev => {
                            const updated = structuredClone(prev);

                            if (!updated.layout?.workflow?.steps?.[targetStepId]?.fields) {
                                console.error(`[RealtimeProvider] Invalid step ${targetStepId} in session context`);
                                return prev;
                            }

                            // Update the field
                            updated.layout.workflow.steps[targetStepId].fields[field] = value;

                            // Dispatch event with step ID included
                            document.dispatchEvent(
                                new CustomEvent("formFieldUpdated", {
                                    detail: {
                                        field,
                                        value,
                                        stepId: targetStepId
                                    },
                                    bubbles: true,
                                    cancelable: true
                                })
                            );

                            return updated;
                        });
                    }
                } catch (err) {
                    console.error("[RealtimeProvider] Failed to process tool response:", err);
                }

                // Update context state so any component can see the new tool response
                setLatestToolResponse(toolMsg);
                break;
            }
            case "response.audio.delta": {
                const audioDelta = message as ResponseAudioDelta;
                // Replace with throttled log
                throttledLog("AUDIO_DELTA", "Received audio delta", { length: audioDelta.delta.length });
                break;
            }
            case "response.interrupt": {
                // Replace with normal log since interrupts are important and infrequent
                console.log("[RealtimeProvider] Received interrupt message");
                setInterruptReceived(true);
                break;
            }
            case "response.audio_transcript.delta": {
                const transcriptDelta = message as ResponseAudioTranscriptDelta;
                // Replace with throttled log
                throttledLog("TRANSCRIPT_DELTA", `Received transcript delta: ${transcriptDelta.delta.slice(0, 20)}...`);
                break;
            }
            case "response.done": {
                const done = message as ResponseDone;
                // Keep normal log for completion events (infrequent)
                console.log("Got response done:", done.response);
                break;
            }
            case "conversation.item.input_audio_transcription.completed": {
                const transcription = message as ResponseInputAudioTranscriptionCompleted;
                // Keep normal log for completion events (infrequent)
                console.log("Got final transcription:", transcription.transcript);
                break;
            }
            // etc. Add more message type handlers as needed
            default:
                throttledLog("UNKNOWN_MESSAGE", `Unhandled message type: ${message.type}`);
        }
    }

    /**
     * Methods you want to expose to child components
     */
    function startSession() {
        sendJsonMessage({
            type: "session.update",
            session: {
                ...sessionContext,
                turn_detection: { type: "server_vad" },
                input_audio_transcription: { model: "whisper-1" }
            }
        });
    }

    function closeWebSocket() {
        const ws = getWebSocket();
        if (ws && ws.readyState === WebSocket.OPEN) {
            console.log("[RealtimeProvider] Manually closing WebSocket");
            ws.close(1000, "User closed session");
        }
    }

    /**
     * The key function: updates local sessionContext AND tracks the source.
     * All context updates MUST go through this function.
     */
    function updateSessionContext(value: SessionContext | ((old: SessionContext) => SessionContext), source: string = "unknown") {
        setSessionContext(prev => {
            // Determine the new context, handling both function and direct object patterns
            const newContext = typeof value === "function" ? value(prev) : value;

            // Create a completely new object to ensure React detects the change
            const updated = JSON.parse(JSON.stringify(newContext));

            // DEBUG: Track step 2 firstName changes
            const oldFirstName = prev?.layout?.workflow?.steps?.[2]?.fields?.firstName;
            const newFirstName = updated?.layout?.workflow?.steps?.[2]?.fields?.firstName;

            // DEBUG: Track step 2 lastName changes
            const oldLastName = prev?.layout?.workflow?.steps?.[2]?.fields?.lastName;
            const newLastName = updated?.layout?.workflow?.steps?.[2]?.fields?.lastName;

            if (oldFirstName !== newFirstName) {
                // Keep this as a regular log since it's specific to firstName changes (less frequent)
                throttledLog("CONTEXT_UPDATE", `Step 2 firstName changing: "${oldFirstName}" → "${newFirstName}"`, {
                    source,
                    timestamp: new Date().toISOString()
                });
            }

            if (oldLastName !== newLastName) {
                throttledLog("CONTEXT_UPDATE", `Step 2 lastName changing: "${oldLastName}" → "${newLastName}"`, {
                    source,
                    timestamp: new Date().toISOString()
                });
            }

            return updated;
        });
    }
    /**
     * Set the connectionId in the session context.
     * This is useful if you want to manage the connectionId outside of this provider.
     */
    function setSessionConnectionId(connectionId: string | null) {
        setSessionContext(prev => {
            const updated = structuredClone(prev);
            // Use an empty string if null, so the type stays consistent.
            updated.connectionId = connectionId ?? "";
            return updated;
        });
    }
    /** Our "public" context value */
    const value: RealtimeContextValue = {
        sendJsonMessage,
        startSession,
        closeWebSocket,
        latestToolResponse,
        interruptReceived,
        sessionContext,
        updateSessionContext,
        setSessionConnectionId
    };

    return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

/**
 * A simple hook to access the RealtimeContext
 */
export function useRealtimeContext() {
    const ctx = useContext(RealtimeContext);
    if (!ctx) {
        throw new Error("useRealtimeContext must be used within a RealtimeProvider");
    }
    return ctx;
}
